/**
 * FileTypeIcon — Styled file-type icon with extension badges for chat messages.
 *
 * Shows a file icon with color-coded extension badge + truncated filename + formatted size.
 * Neubrutalism styling consistent with the chat UI.
 */

import { memo, useMemo } from 'react';
import { FILE_TYPE_COLORS } from '@/constants/theme';

interface FileTypeIconProps {
  fileName: string;
  fileSize?: number;
  url?: string;
  isMine?: boolean;
  /** Compact vertical layout for preview grids */
  compact?: boolean;
}

interface FileTypeConfig {
  color: string;
  bgColor: string;
  borderColor: string;
  label: string;
}

function getExtension(fileName: string): string {
  const parts = fileName.split('.');
  return parts.length > 1 ? parts.pop()!.toLowerCase() : '';
}

function getFileTypeConfig(ext: string): FileTypeConfig {
  const colors = FILE_TYPE_COLORS[ext] || FILE_TYPE_COLORS.default;
  return {
    color: colors.text,
    bgColor: colors.bg,
    borderColor: colors.border,
    label: ext.toUpperCase() || 'FILE',
  };
}

function formatFileSize(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const FileTypeIcon = memo(function FileTypeIcon({
  fileName,
  fileSize,
  url,
  isMine = false,
  compact = false,
}: FileTypeIconProps) {
  const ext = useMemo(() => getExtension(fileName), [fileName]);
  const config = useMemo(() => getFileTypeConfig(ext), [ext]);
  const sizeStr = useMemo(() => formatFileSize(fileSize), [fileSize]);

  // Compact vertical layout for preview grids
  if (compact) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-1.5 text-neutral-800">
        <div className="relative">
          <svg
            className="h-10 w-10"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
          <span
            className={`absolute -bottom-1 -end-1 rounded px-1 py-0.5 text-[8px] font-black leading-none ${config.bgColor} ${config.color} border ${config.borderColor}`}
          >
            {config.label}
          </span>
        </div>
        <div className="max-w-full truncate px-1 text-[10px] font-bold text-neutral-800/70">
          {fileName}
        </div>
        {sizeStr && (
          <div className="text-[9px] text-neutral-800/50">{sizeStr}</div>
        )}
      </div>
    );
  }

  // Full horizontal layout for message bubbles — fixed height like WhatsApp document row
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={`flex h-[4.5rem] items-center gap-3 rounded-lg border px-3 transition-colors ${
        isMine
          ? 'border-white/30 bg-white/10 text-white hover:bg-white/20'
          : 'border-white/20 bg-white/30 text-neutral-800 hover:bg-white/30'
      }`}
    >
      {/* File icon with extension badge */}
      <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg border ${config.borderColor} ${config.bgColor}`}>
        <svg
          className={`h-6 w-6 ${config.color}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
          <polyline points="14 2 14 8 20 8" />
        </svg>
      </div>

      {/* File info */}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-bold" dir="auto">
          {fileName}
        </div>
        <div className={`flex items-center gap-1.5 text-xs ${isMine ? 'text-white/60' : 'text-neutral-800/50'}`}>
          <span className={`font-bold ${config.color}`}>{config.label}</span>
          {sizeStr && (
            <>
              <span>·</span>
              <span>{sizeStr}</span>
            </>
          )}
        </div>
      </div>

      {/* Download arrow */}
      <svg
        className={`h-4 w-4 flex-shrink-0 ${isMine ? 'text-white/60' : 'text-neutral-800/40'}`}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </svg>
    </a>
  );
});

export default FileTypeIcon;
